import { FunctionComponent } from 'react';
import { JobPosition } from '@restworld/utility-types';
import { BreadcrumbJsonLd } from 'next-seo';
import {
  OCCUPATION_PAGES,
  ALL_OCCUPATION_SLUGS,
  OCCUPATION_CITY_COMBOS,
  ROLES,
  CITIES,
  ALL_CITY_SLUGS,
} from '../lib/seo/category-config';
import { computeValidThrough, toCountryCode } from '../lib/seo/job-posting-fields';

interface Props {
  jobPositionStatic: Partial<JobPosition>;
  slug?: string;
}
export const JobPositionSEO: FunctionComponent<Props> = ({ jobPositionStatic: jobPosition, slug }) => {
  const occupation = jobPosition?.occupation?.label_it?.label;
  const title = jobPosition?.job_title || occupation;
  const showRestaurantDetails = jobPosition?.show_restaurant_details || false;
  const restaurant = jobPosition?.restaurant;

  // Build smart breadcrumb based on JP's occupation, domain, and city
  const breadcrumbItems = buildBreadcrumbItems({
    title: title || 'Posizione',
    slug,
    occupationId: jobPosition?.occupation?.id?.toString(),
    domainInterestKey: jobPosition?.domain_interest_var?.key,
    locality: restaurant?.locality,
  });

  const baseSalary = buildBaseSalary(jobPosition);
  const employmentType = getEmploymentType(jobPosition?.job_type_var?.key, jobPosition?.contract_type_var?.key);

  // Prefer `first_published_at` (Restworld go-live) over `published_at`: for
  // ATS-imported JPs `published_at` can be the (stale) source-ATS publication
  // date. See field doc in `libs/utility-types/src/employers.ts`.
  const restworldPublishedAt = jobPosition?.first_published_at ?? jobPosition?.published_at;
  const validThrough = computeValidThrough({
    statusKey: jobPosition?.status_key,
    wentOfflineAt: jobPosition?.went_offline_at,
    publishedAt: restworldPublishedAt,
  });

  const addressCountry = toCountryCode(restaurant?.country);

  // Omit the logo entirely when there isn't one: an empty `logo: ""` is flagged
  // by Google's rich-results validation.
  const organizationLogo = showRestaurantDetails
    ? restaurant?.restaurant_logo
    : 'https://restworld-illustrations.fra1.cdn.digitaloceanspaces.com/logo/rw_viola.png';

  const jobPostingJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'JobPosting',
    title: title ?? '',
    description:
      '<p>' + (jobPosition?.job_description || '') + '<br />' + (jobPosition?.job_contract_description || '') + '</p>',
    datePosted: restworldPublishedAt || jobPosition?.inserted_at,
    ...(validThrough ? { validThrough } : {}),
    directApply: true,
    employmentType,
    // Google recommends a stable per-posting identifier.
    ...(jobPosition?.id ? { identifier: { '@type': 'PropertyValue', name: 'Restworld', value: jobPosition.id } } : {}),
    hiringOrganization: {
      '@type': 'Organization',
      name: showRestaurantDetails ? restaurant?.restaurant_name : 'Restworld',
      sameAs: showRestaurantDetails ? restaurant?.website_url : 'https://www.restworld.it/',
      ...(organizationLogo ? { logo: organizationLogo } : {}),
    },
    jobLocation: {
      '@type': 'Place',
      address: {
        '@type': 'PostalAddress',
        // Postal code and street address only on a non-anonymous listing: on an
        // anonymous one they are no longer in the page payload at all (see
        // `lib/job-position/minify-job-position.ts`), and an empty string here
        // would be invalid data rather than a softer version of it. Google for
        // Jobs asks for locality, region and country, which stay in both cases.
        ...(showRestaurantDetails && restaurant?.postal_code ? { postalCode: restaurant.postal_code } : {}),
        addressLocality: restaurant?.locality ?? '',
        // Google for Jobs expects an ISO 3166-1 alpha-2 country code, but
        // `restaurant.country` is a free-text name stored inconsistently in IT
        // or EN ("Italia"/"Italy"). Omitted when we cannot map it: a free-text
        // name in this field is invalid data, not a softer version of it.
        ...(addressCountry ? { addressCountry } : {}),
        addressRegion: restaurant?.administrative_area_level_1 ?? '',
        ...(showRestaurantDetails && restaurant?.address ? { streetAddress: restaurant.address } : {}),
      },
    },
    ...(baseSalary ? { baseSalary } : {}),
  };

  return (
    <>
      <BreadcrumbJsonLd itemListElements={breadcrumbItems} />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jobPostingJsonLd).replace(/<\//g, '<\\/') }}
      />
    </>
  );
};

/**
 * Build breadcrumb items linking to the most relevant category page.
 *
 * Priority: occupation macro-page > domain interest > generic.
 * If the JP's city matches a known city slug, adds a city-level breadcrumb.
 *
 * Example results:
 * - Home > Lavoro Cameriere a Milano > Cameriere per ristorante XYZ
 * - Home > Lavoro Cucina > Sous chef per hotel ABC
 * - Home > Offerte di lavoro > Posizione XYZ (fallback)
 */
function buildBreadcrumbItems(params: {
  title: string;
  slug?: string;
  occupationId?: string;
  domainInterestKey?: string;
  locality?: string;
}) {
  const { title, slug, occupationId, domainInterestKey, locality } = params;
  const items: { position: number; name: string; item: string }[] = [
    { position: 1, name: 'Home', item: 'https://www.restworld.it/' },
  ];

  // Find the best matching category page
  const citySlug = locality ? findCitySlug(locality) : null;
  const occupationSlug = occupationId ? findOccupationSlug(occupationId) : null;
  const roleSlug = domainInterestKey ? findRoleSlug(domainInterestKey) : null;

  if (occupationSlug) {
    const occConfig = OCCUPATION_PAGES[occupationSlug];
    // Only link occupation+city combos for cities that have static pages
    const hasOccCityPage = citySlug && OCCUPATION_CITY_COMBOS.includes(citySlug);
    if (hasOccCityPage) {
      items.push({
        position: 2,
        name: `Lavoro ${occConfig.metaLabel} a ${CITIES[citySlug].label}`,
        item: `https://www.restworld.it/cerco-lavoro/${occupationSlug}/${citySlug}`,
      });
    } else {
      items.push({
        position: 2,
        name: `Lavoro ${occConfig.metaLabel}`,
        item: `https://www.restworld.it/cerco-lavoro/${occupationSlug}`,
      });
    }
  } else if (roleSlug) {
    const roleConfig = ROLES[roleSlug];
    if (citySlug) {
      items.push({
        position: 2,
        name: `Lavoro ${roleConfig.label} a ${CITIES[citySlug].label}`,
        item: `https://www.restworld.it/cerco-lavoro/${roleSlug}/${citySlug}`,
      });
    } else {
      items.push({
        position: 2,
        name: `Lavoro ${roleConfig.label}`,
        item: `https://www.restworld.it/cerco-lavoro/${roleSlug}`,
      });
    }
  } else {
    items.push({
      position: 2,
      name: 'Offerte di lavoro',
      item: 'https://www.restworld.it/cerco-lavoro',
    });
  }

  items.push({
    position: 3,
    name: title,
    item: slug ? `https://www.restworld.it/posizione/${slug}` : 'https://www.restworld.it/cerco-lavoro',
  });

  return items;
}

/**
 * Find the occupation macro-page slug that contains this occupation ID.
 */
function findOccupationSlug(occupationId: string): string | null {
  for (const slug of ALL_OCCUPATION_SLUGS) {
    if (OCCUPATION_PAGES[slug].occupationIds.includes(occupationId)) {
      return slug;
    }
  }
  return null;
}

/**
 * Find the role slug matching a domain_interest key.
 */
function findRoleSlug(domainKey: string): string | null {
  for (const [slug, config] of Object.entries(ROLES)) {
    if (config.domainKey === domainKey) return slug;
  }
  return null;
}

/**
 * Find a city slug matching a locality name (case-insensitive).
 */
function findCitySlug(locality: string): string | null {
  const normalized = locality.toLowerCase().trim();
  for (const slug of ALL_CITY_SLUGS) {
    if (CITIES[slug].label.toLowerCase() === normalized) return slug;
  }
  return null;
}

const buildBaseSalary = (jobPosition: Partial<JobPosition>) => {
  const unitText = formatSalaryUnit(jobPosition);
  const min = jobPosition.min_salary ? Number(jobPosition.min_salary) : null;
  const max = jobPosition.max_salary ? Number(jobPosition.max_salary) : null;
  const fixed = jobPosition.salary ? Number(jobPosition.salary) : null;

  if (min && max && min !== max) {
    return {
      '@type': 'MonetaryAmount',
      currency: 'EUR',
      value: { '@type': 'QuantitativeValue', unitText, minValue: min, maxValue: max },
    };
  }
  const value = fixed || min || max;
  if (!value) return null;
  return {
    '@type': 'MonetaryAmount',
    currency: 'EUR',
    value: { '@type': 'QuantitativeValue', unitText, value },
  };
};

const formatSalaryUnit = (jobPosition: Partial<JobPosition>) => {
  // https://developers.google.com/search/docs/data-types/job-posting?hl=en
  switch (jobPosition?.payment_type_var?.key) {
    case 'hour':
      return 'HOUR';
    case 'service':
      return 'DAY';
    case 'annual':
      return 'YEAR';
    default:
      return 'MONTH';
  }
};

const getEmploymentType = (jobTypeKey?: string, contractTypeKey?: string): string | string[] => {
  const types: string[] = [];

  // Hourly type from job_type_var.key
  if (jobTypeKey === 'full_time' || jobTypeKey === 'weekend_full_time') {
    types.push('FULL_TIME');
  } else if (
    jobTypeKey === 'part_time' ||
    jobTypeKey === 'part_time_horizontal' ||
    jobTypeKey === 'part_time_veritical' ||
    jobTypeKey === 'on_call' ||
    jobTypeKey?.startsWith('weekend')
  ) {
    types.push('PART_TIME');
  }

  // Contract type from contract_type_var.key
  if (contractTypeKey?.startsWith('fixed_term') || contractTypeKey === 'on_call') {
    if (!types.includes('TEMPORARY')) types.push('TEMPORARY');
  } else if (contractTypeKey === 'apprenticeship' || contractTypeKey === 'internship') {
    if (!types.includes('INTERN')) types.push('INTERN');
  }

  if (types.length === 0) types.push('FULL_TIME');
  return types.length === 1 ? types[0] : types;
};
